import React, { useState, memo, useRef, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowBack,
  Delete,
  CameraAlt,
  Edit,
  Save,
  Close,
  Person,
  Email,
  Phone,
  LocationOn,
  Business,
  Public,
  Security,
  CheckCircle,
  Warning,
  Info,
  PhotoCamera,
  Visibility,
  VisibilityOff,
  Refresh,
  Settings,
} from "@mui/icons-material";
import {
  AppBar,
  Box,
  Button,
  CircularProgress,
  Paper,
  TextField,
  Toolbar,
  IconButton,
  Typography,
  Card,
  CardContent,
  Avatar,
  Grid,
  Divider,
  Chip,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Skeleton,
  Fade,
  Slide,
  useTheme,
  useMediaQuery,
  LinearProgress,
  Tooltip,
} from "@mui/material";
import BreadcrumbsNav from "../Components/Buttons/Breadcrumbs";
import { useGetMe } from "../Context/GetMeContext";
import logo from "../Assets/Logo/logo.jpg";
import CustomModal from "../Components/CustomModal";
import Preloader from "../Components/Preloader/Preloader";
import UpdateUserPassword from "../Components/user/UpdateUserPassword";
import { gql, useMutation, useQuery } from "@apollo/client";
import { useNotification } from "../Context/notificationContext";
import {
  API_URL,
  IMAGES_UPLOAD_API_URL,
  PROFILE_UPLOAD_API_URL,
} from "../constants";
import BreadCrumb from "../Components/BreadCrumb";
import { motion, AnimatePresence } from "framer-motion";

const UPDATE_USER_DATA = gql`
  mutation updateUser($id: ID!, $userData: String!) {
    updateUser(id: $id, userData: $userData) {
      id
      name
      email
      phone
      address
      city
      state
      country
      profile_pic
    }
  }
`;

// Enhanced Error Boundary Component
const ErrorBoundary = ({ children, error, retry }) => {
  if (error) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Card
          sx={{
            m: 2,
            background: "linear-gradient(135deg, #ff6b6b20, #ee5a5220)",
            border: "1px solid #ff6b6b40",
            borderRadius: 3,
          }}
        >
          <CardContent sx={{ textAlign: "center", py: 4 }}>
            <Warning sx={{ fontSize: 48, color: "#ff6b6b", mb: 2 }} />
            <Typography variant="h6" gutterBottom color="error">
              Unable to Load Profile
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {error?.message ||
                "Something went wrong while loading your profile"}
            </Typography>
            <Button
              variant="contained"
              onClick={retry}
              startIcon={<Refresh />}
              sx={{
                background: "linear-gradient(45deg, #ff6b6b, #ee5a52)",
                borderRadius: 2,
              }}
            >
              Try Again
            </Button>
          </CardContent>
        </Card>
      </motion.div>
    );
  }
  return children;
};

// Enhanced Loading Skeleton
const ProfileSkeleton = () => (
  <Box sx={{ p: 3 }}>
    <Box display="flex" flexDirection={{ xs: "column", md: "row" }} gap={4}>
      <Box flex={1} display="flex" flexDirection="column" alignItems="center">
        <Skeleton variant="circular" width={150} height={150} sx={{ mb: 2 }} />
        <Skeleton variant="text" width="80%" height={24} sx={{ mb: 1 }} />
        <Skeleton variant="text" width="60%" height={16} sx={{ mb: 2 }} />
        <Skeleton
          variant="rectangular"
          width="100%"
          height={40}
          sx={{ mb: 1 }}
        />
        <Skeleton variant="rectangular" width="100%" height={40} />
      </Box>
      <Box flex={2}>
        {[1, 2, 3, 4, 5, 6, 7].map((item) => (
          <Skeleton
            key={item}
            variant="rectangular"
            height={56}
            sx={{ mb: 2, borderRadius: 1 }}
          />
        ))}
      </Box>
    </Box>
  </Box>
);

// Enhanced validation functions
const validationRules = {
  name: (value) => {
    if (!value?.trim()) return "Name is required";
    if (value.length < 2) return "Name must be at least 2 characters";
    if (value.length > 50) return "Name must be less than 50 characters";
    if (!/^[a-zA-Z\s'-]+$/.test(value))
      return "Name can only contain letters, spaces, hyphens, and apostrophes";
    return null;
  },
  email: (value) => {
    if (!value?.trim()) return "Email is required";
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) return "Please enter a valid email address";
    return null;
  },
  phone: (value) => {
    if (!value?.trim()) return "Phone number is required";
    const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
    if (!phoneRegex.test(value.replace(/[\s\-\(\)]/g, ""))) {
      return "Please enter a valid phone number";
    }
    return null;
  },
  address: (value) => {
    if (value && value.length > 100)
      return "Address must be less than 100 characters";
    return null;
  },
  city: (value) => {
    if (value && value.length > 50)
      return "City must be less than 50 characters";
    if (value && !/^[a-zA-Z\s'-]+$/.test(value))
      return "City can only contain letters, spaces, hyphens, and apostrophes";
    return null;
  },
  state: (value) => {
    if (value && value.length > 50)
      return "State must be less than 50 characters";
    if (value && !/^[a-zA-Z\s'-]+$/.test(value))
      return "State can only contain letters, spaces, hyphens, and apostrophes";
    return null;
  },
  country: (value) => {
    if (value && value.length > 50)
      return "Country must be less than 50 characters";
    if (value && !/^[a-zA-Z\s'-]+$/.test(value))
      return "Country can only contain letters, spaces, hyphens, and apostrophes";
    return null;
  },
};

const Profile = memo(() => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [editable, setEditable] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const navigate = useNavigate();
  const { user, refetch } = useGetMe();
  const userdata = user?.me;
  const fileInputRef = useRef(null);
  const { showNotification } = useNotification();

  const [editData, setEditData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    country: "",
    profile_pic: "",
  });

  // Initialize form data
  useEffect(() => {
    if (userdata) {
      setEditData({
        name: userdata.name || "",
        email: userdata.email || "",
        phone: userdata.phone || "",
        address: userdata.address || "",
        city: userdata.city || "",
        state: userdata.state || "",
        country: userdata.country || "",
        profile_pic: userdata.profile_pic || "",
      });
    }
  }, [userdata]);

  // Enhanced notification handler
  const showNotificationMessage = useCallback(
    (severity, message, duration = 5000) => {
      setNotification({
        open: true,
        message,
        severity,
      });
      setTimeout(() => {
        setNotification((prev) => ({ ...prev, open: false }));
      }, duration);
    },
    []
  );

  // Enhanced validation
  const validateField = useCallback((fieldName, value) => {
    const validator = validationRules[fieldName];
    return validator ? validator(value) : null;
  }, []);

  const validateAllFields = useCallback(() => {
    const errors = {};
    Object.keys(editData).forEach((field) => {
      if (field !== "profile_pic") {
        const error = validateField(field, editData[field]);
        if (error) {
          errors[field] = error;
        }
      }
    });
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [editData, validateField]);

  const handleInitiseEdit = useCallback(() => {
    setEditable(!editable);
    if (!editable && userdata) {
      // Reset form data when entering edit mode
      setEditData({
        name: userdata.name || "",
        email: userdata.email || "",
        phone: userdata.phone || "",
        address: userdata.address || "",
        city: userdata.city || "",
        state: userdata.state || "",
        country: userdata.country || "",
        profile_pic: userdata.profile_pic || "",
      });
      setValidationErrors({});
    }
  }, [editable, userdata]);

  const handleInputChange = useCallback(
    (e) => {
      const { name, value } = e.target;

      // Special handling for phone field
      if (name === "phone") {
        // Allow only numbers, spaces, hyphens, parentheses, and plus sign
        const phoneValue = value.replace(/[^\d\s\-\(\)\+]/g, "");
        setEditData((prev) => ({ ...prev, [name]: phoneValue }));
      } else {
        setEditData((prev) => ({ ...prev, [name]: value }));
      }

      // Clear field-specific error when user starts typing
      if (validationErrors[name]) {
        setValidationErrors((prev) => ({ ...prev, [name]: null }));
      }
    },
    [validationErrors]
  );

  const [updateUser, { loading }] = useMutation(UPDATE_USER_DATA, {
    onError: (error) => {
      showNotificationMessage("error", `Update failed: ${error.message}`);
    },
  });

  const handleUpdateProfile = useCallback(async () => {
    try {
      if (!validateAllFields()) {
        showNotificationMessage(
          "error",
          "Please fix the validation errors before saving"
        );
        return;
      }

      const userData = JSON.stringify(editData);
      await updateUser({ variables: { id: userdata.id, userData } });

      showNotificationMessage("success", "Profile updated successfully!");
      await refetch();
      setEditable(false);
    } catch (error) {
      console.error("Profile update error:", error);
      showNotificationMessage(
        "error",
        error.message || "Failed to update profile"
      );
    }
  }, [
    editData,
    updateUser,
    userdata?.id,
    refetch,
    validateAllFields,
    showNotificationMessage,
  ]);

  const handleImageUpload = useCallback(
    async (file) => {
      if (!file) return;

      // Enhanced file validation
      const maxSize = 5 * 1024 * 1024; // 5MB
      const allowedTypes = [
        "image/png",
        "image/jpeg",
        "image/jpg",
        "image/webp",
      ];

      if (!allowedTypes.includes(file.type)) {
        showNotificationMessage(
          "error",
          "Only PNG, JPEG, JPG, and WebP files are allowed"
        );
        return;
      }

      if (file.size > maxSize) {
        showNotificationMessage("error", "File size must be less than 5MB");
        return;
      }

      const formData = new FormData();
      formData.append("image", file);

      setUploadingImage(true);

      try {
        const response = await fetch(
          `${PROFILE_UPLOAD_API_URL}${userdata.id}`,
          {
            method: "POST",
            body: formData,
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(
            errorData.message || `Upload failed with status ${response.status}`
          );
        }

        const data = await response.json();
        if (data.success) {
          showNotificationMessage(
            "success",
            "Profile picture updated successfully!"
          );
          await refetch();
        } else {
          throw new Error(data.message || "Upload failed");
        }
      } catch (error) {
        console.error("Image upload error:", error);
        showNotificationMessage(
          "error",
          error.message || "Failed to upload image"
        );
      } finally {
        setUploadingImage(false);
      }
    },
    [userdata?.id, refetch, showNotificationMessage]
  );

  const handleRemoveImage = useCallback(async () => {
    try {
      const userData = JSON.stringify({ ...editData, profile_pic: null });
      await updateUser({ variables: { id: userdata.id, userData } });
      showNotificationMessage(
        "success",
        "Profile picture removed successfully!"
      );
      await refetch();
    } catch (error) {
      console.error("Image removal error:", error);
      showNotificationMessage(
        "error",
        error.message || "Failed to remove profile picture"
      );
    }
  }, [editData, updateUser, userdata?.id, refetch, showNotificationMessage]);

  // Field configuration for dynamic rendering
  const formFields = [
    {
      name: "name",
      label: "Full Name",
      type: "text",
      icon: <Person />,
      required: true,
      color: "#2196f3",
    },
    {
      name: "email",
      label: "Email Address",
      type: "email",
      icon: <Email />,
      required: true,
      color: "#ff9800",
    },
    {
      name: "phone",
      label: "Phone Number",
      type: "tel",
      icon: <Phone />,
      required: true,
      color: "#4caf50",
    },
    {
      name: "address",
      label: "Address",
      type: "text",
      icon: <LocationOn />,
      color: "#9c27b0",
    },
    {
      name: "city",
      label: "City",
      type: "text",
      icon: <Business />,
      color: "#00bcd4",
    },
    {
      name: "state",
      label: "State/Province",
      type: "text",
      icon: <Business />,
      color: "#795548",
    },
    {
      name: "country",
      label: "Country",
      type: "text",
      icon: <Public />,
      color: "#607d8b",
    },
  ];

  if (!userdata) {
    return <ProfileSkeleton />;
  }

  return (
    <ErrorBoundary error={user?.error} retry={refetch}>
      <BreadCrumb
        links={[{ label: "Dashboard", href: "/dashboard" }]}
        currentPage="Profile"
      />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            overflow: "hidden",
            background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
            minHeight: "calc(100vh - 100px)",
          }}
        >
          {/* Header Section */}
          <Box
            sx={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "white",
              p: 4,
              position: "relative",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: -50,
                right: -50,
                width: 200,
                height: 200,
                borderRadius: "50%",
                background: "rgba(255,255,255,0.1)",
              }}
            />

            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={4}>
                <Box display="flex" flexDirection="column" alignItems="center">
                  <Box sx={{ position: "relative", mb: 2 }}>
                    {uploadingImage ? (
                      <Box
                        sx={{
                          width: 150,
                          height: 150,
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          background: "rgba(255,255,255,0.2)",
                          backdropFilter: "blur(10px)",
                        }}
                      >
                        <CircularProgress color="inherit" size={40} />
                      </Box>
                    ) : (
                      <Avatar
                        src={userdata.profile_pic || logo}
                        alt="Profile"
                        sx={{
                          width: 150,
                          height: 150,
                          border: "4px solid rgba(255,255,255,0.3)",
                          transition: "all 0.3s ease",
                          "&:hover": {
                            transform: "scale(1.05)",
                            border: "4px solid rgba(255,255,255,0.5)",
                          },
                        }}
                      />
                    )}

                    <Box
                      sx={{
                        position: "absolute",
                        bottom: 0,
                        right: 0,
                        display: "flex",
                        gap: 1,
                      }}
                    >
                      <Tooltip title="Upload new photo">
                        <IconButton
                          onClick={() => fileInputRef.current?.click()}
                          sx={{
                            background: "rgba(255,255,255,0.9)",
                            color: "#667eea",
                            width: 40,
                            height: 40,
                            "&:hover": {
                              background: "white",
                              transform: "scale(1.1)",
                            },
                          }}
                        >
                          <CameraAlt />
                        </IconButton>
                      </Tooltip>

                      {userdata.profile_pic && (
                        <Tooltip title="Remove photo">
                          <IconButton
                            onClick={handleRemoveImage}
                            sx={{
                              background: "rgba(244, 67, 54, 0.9)",
                              color: "white",
                              width: 40,
                              height: 40,
                              "&:hover": {
                                background: "#f44336",
                                transform: "scale(1.1)",
                              },
                            }}
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>

                  <input
                    type="file"
                    ref={fileInputRef}
                    hidden
                    accept="image/png, image/jpeg, image/jpg, image/webp"
                    onChange={(e) => handleImageUpload(e.target.files[0])}
                  />

                  <Typography
                    variant="h5"
                    fontWeight={700}
                    gutterBottom
                    textAlign="center"
                  >
                    {userdata.name || "Anonymous User"}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{ opacity: 0.9, mb: 2, textAlign: "center" }}
                  >
                    {userdata.email}
                  </Typography>

                  <Chip
                    icon={<CheckCircle />}
                    label="Verified Account"
                    sx={{
                      background: "rgba(76, 175, 80, 0.2)",
                      color: "white",
                      border: "1px solid rgba(76, 175, 80, 0.5)",
                      mb: 3,
                    }}
                  />

                  <Box
                    display="flex"
                    gap={2}
                    flexWrap="wrap"
                    justifyContent="center"
                  >
                    <Button
                      variant="contained"
                      startIcon={<Security />}
                      onClick={() => setShowPasswordModal(true)}
                      sx={{
                        background: "rgba(255,255,255,0.2)",
                        backdropFilter: "blur(10px)",
                        border: "1px solid rgba(255,255,255,0.3)",
                        color: "white",
                        "&:hover": {
                          background: "rgba(255,255,255,0.3)",
                          transform: "translateY(-2px)",
                        },
                      }}
                    >
                      Change Password
                    </Button>

                    <Button
                      variant="contained"
                      startIcon={<Settings />}
                      onClick={() => navigate("/settings")}
                      sx={{
                        background: "rgba(255, 152, 0, 0.2)",
                        backdropFilter: "blur(10px)",
                        border: "1px solid rgba(255, 152, 0, 0.3)",
                        color: "white",
                        "&:hover": {
                          background: "rgba(255, 152, 0, 0.3)",
                          transform: "translateY(-2px)",
                        },
                      }}
                    >
                      Settings
                    </Button>

                    <Button
                      variant="contained"
                      startIcon={editable ? <Close /> : <Edit />}
                      onClick={handleInitiseEdit}
                      sx={{
                        background: editable
                          ? "linear-gradient(45deg, #f44336, #d32f2f)"
                          : "linear-gradient(45deg, #4caf50, #388e3c)",
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: "0 6px 20px rgba(0,0,0,0.3)",
                        },
                      }}
                    >
                      {editable ? "Cancel" : "Edit Profile"}
                    </Button>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={8}>
                <Box sx={{ pl: { md: 4 } }}>
                  <Typography variant="h4" fontWeight={700} gutterBottom>
                    Profile Information
                  </Typography>
                  <Typography variant="body1" sx={{ opacity: 0.9, mb: 3 }}>
                    {editable
                      ? "Update your personal information below"
                      : "Your personal information and contact details"}
                  </Typography>

                  {/* Profile Stats */}
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    {[
                      {
                        label: "Member Since",
                        value: "2023",
                        icon: <CheckCircle />,
                      },
                      {
                        label: "Profile Views",
                        value: "1,247",
                        icon: <Visibility />,
                      },
                      {
                        label: "Last Updated",
                        value: "Today",
                        icon: <Refresh />,
                      },
                    ].map((stat, index) => (
                      <Grid item xs={4} key={stat.label}>
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.4, delay: index * 0.1 }}
                        >
                          <Paper
                            sx={{
                              p: 2,
                              textAlign: "center",
                              background: "rgba(255,255,255,0.2)",
                              backdropFilter: "blur(10px)",
                              border: "1px solid rgba(255,255,255,0.3)",
                              borderRadius: 2,
                            }}
                          >
                            <Box sx={{ color: "white", mb: 1 }}>
                              {stat.icon}
                            </Box>
                            <Typography
                              variant="h6"
                              fontWeight={600}
                              color="white"
                            >
                              {stat.value}
                            </Typography>
                            <Typography variant="caption" sx={{ opacity: 0.8 }}>
                              {stat.label}
                            </Typography>
                          </Paper>
                        </motion.div>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Form Section */}
          <Box sx={{ p: 4 }}>
            <Grid container spacing={3}>
              {formFields.map((field, index) => (
                <Grid item xs={12} md={6} key={field.name}>
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.05 }}
                  >
                    <Card
                      sx={{
                        borderRadius: 3,
                        transition: "all 0.3s ease",
                        border: `2px solid ${
                          editable ? `${field.color}20` : "transparent"
                        }`,
                        "&:hover": {
                          transform: editable ? "translateY(-2px)" : "none",
                          boxShadow: editable
                            ? "0 8px 25px rgba(0,0,0,0.1)"
                            : "none",
                        },
                      }}
                    >
                      <CardContent sx={{ p: 3 }}>
                        <TextField
                          fullWidth
                          label={field.label}
                          type={field.type}
                          value={
                            editable
                              ? editData[field.name] || ""
                              : userdata[field.name] || ""
                          }
                          variant={editable ? "outlined" : "standard"}
                          disabled={!editable}
                          name={field.name}
                          onChange={handleInputChange}
                          error={!!validationErrors[field.name]}
                          helperText={validationErrors[field.name]}
                          required={field.required}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Box
                                  sx={{
                                    color: field.color,
                                    display: "flex",
                                    alignItems: "center",
                                  }}
                                >
                                  {field.icon}
                                </Box>
                              </InputAdornment>
                            ),
                            readOnly: !editable,
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: editable
                                ? "rgba(255,255,255,0.8)"
                                : "transparent",
                              "& fieldset": {
                                borderColor: editable
                                  ? `${field.color}40`
                                  : "transparent",
                              },
                              "&:hover fieldset": {
                                borderColor: editable
                                  ? `${field.color}60`
                                  : "transparent",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: field.color,
                                borderWidth: 2,
                              },
                            },
                            "& .MuiInput-underline": {
                              "&:before": {
                                borderBottomColor: "transparent",
                              },
                              "&:after": {
                                borderBottomColor: "transparent",
                              },
                              "&:hover:not(.Mui-disabled):before": {
                                borderBottomColor: "transparent",
                              },
                            },
                            "& .MuiInputLabel-root": {
                              color: field.color,
                              fontWeight: 600,
                            },
                            "& .MuiFormHelperText-root": {
                              color: validationErrors[field.name]
                                ? "#f44336"
                                : field.color,
                              fontWeight: 500,
                            },
                          }}
                        />
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>

            {/* Save Button */}
            <AnimatePresence>
              {editable && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 20 }}
                  transition={{ duration: 0.3 }}
                >
                  <Box
                    sx={{ display: "flex", justifyContent: "center", mt: 4 }}
                  >
                    <Button
                      variant="contained"
                      size="large"
                      startIcon={
                        loading ? <CircularProgress size={20} /> : <Save />
                      }
                      onClick={handleUpdateProfile}
                      disabled={
                        loading ||
                        Object.keys(validationErrors).some(
                          (key) => validationErrors[key]
                        )
                      }
                      sx={{
                        background: "linear-gradient(45deg, #667eea, #764ba2)",
                        px: 6,
                        py: 2,
                        borderRadius: 3,
                        fontSize: "1.1rem",
                        fontWeight: 600,
                        textTransform: "none",
                        boxShadow: "0 8px 25px rgba(102, 126, 234, 0.4)",
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: "0 12px 35px rgba(102, 126, 234, 0.6)",
                        },
                        "&:disabled": {
                          background: "linear-gradient(45deg, #ccc, #999)",
                          transform: "none",
                        },
                      }}
                    >
                      {loading ? "Saving Changes..." : "Save Changes"}
                    </Button>
                  </Box>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Info Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <Card
                sx={{
                  mt: 4,
                  background:
                    "linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)",
                  border: "1px solid #2196f330",
                  borderRadius: 3,
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      mb: 2,
                    }}
                  >
                    <Info sx={{ color: "#2196f3", fontSize: 28 }} />
                    <Typography variant="h6" fontWeight={600} color="#1976d2">
                      Profile Tips
                    </Typography>
                  </Box>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    Keep your profile information up to date to ensure the best
                    experience:
                  </Typography>

                  <Box component="ul" sx={{ m: 0, pl: 2 }}>
                    <Typography
                      component="li"
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      Use a clear, professional profile photo
                    </Typography>
                    <Typography
                      component="li"
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      Keep your contact information current
                    </Typography>
                    <Typography
                      component="li"
                      variant="body2"
                      color="text.secondary"
                    >
                      Update your password regularly for security
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </motion.div>
          </Box>
        </Paper>
      </motion.div>

      {/* Password Modal */}
      <CustomModal
        open={showPasswordModal}
        handleClose={() => setShowPasswordModal(false)}
      >
        <UpdateUserPassword id={userdata?.id} />
      </CustomModal>

      {/* Enhanced Notification */}
      <Snackbar
        open={notification.open}
        autoHideDuration={5000}
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setNotification({ ...notification, open: false })}
          severity={notification.severity}
          variant="filled"
          sx={{
            width: "100%",
            borderRadius: 2,
            fontWeight: 500,
          }}
          icon={
            notification.severity === "success" ? (
              <CheckCircle />
            ) : notification.severity === "error" ? (
              <Warning />
            ) : notification.severity === "warning" ? (
              <Warning />
            ) : (
              <Info />
            )
          }
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </ErrorBoundary>
  );
});

Profile.displayName = "Profile";

export default Profile;
